import React from "react";
import {
  Container,
  Title,
  Text,
  Image,
  Stack,
  Box,
  Paper,
  Button,
  rem,
  Badge,
  Grid,
} from "@mantine/core";
import * as Icons from "@tabler/icons-react";
import { useTranslation } from "../context/TranslationContext";

export default function About() {
  const { t } = useTranslation();

  return (
    <Box pb={rem(100)}>
      {/* 1. HEADER SECTION */}
      <Container size="lg" pt={rem(80)} pb={rem(40)}>
        <Stack gap="xs" align="center">
          <Badge variant="dot" color="blue" size="lg">
            {t("aboutTitle", "Our History")}
          </Badge>
          <Title
            order={1}
            size={rem(48)}
            ta="center"
            fw={900}
            style={{ letterSpacing: rem(-1) }}
          >
            {t("aboutHeroTitle", "Swiss Dairy Products with Indian Soul")}
          </Title>
          <Text c="dimmed" size="lg" mt="xs" fw={500} italic>
            {t(
              "aboutTagline",
              "The journey of bringing authentic Indian dairy to Switzerland"
            )}
          </Text>
        </Stack>
      </Container>

      {/* 2. THE STORY JOURNEY */}
      <Container size="lg" mt={rem(40)}>
        <Stack gap={rem(100)}>
          {/* Section A: The Vision */}
          <Grid gutter={80} align="center">
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Title order={2} mb="md" size="h1">
                {t("storyVision", "Our Vision")}
              </Title>
              <Text size="lg" lh={1.8} c="gray.8">
                {t("storyPara1")}
              </Text>
              <Text size="lg" lh={1.8} c="gray.8" mt="md">
                {t("storyPara2")}
              </Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Paper shadow="xl" radius="lg" p="xs" withBorder>
                <Image src="/images/logo.png" alt="Yogi Foods Logo" />
              </Paper>
            </Grid.Col>
          </Grid>

          {/* Section B: Crafting Authenticity */}
          <Grid gutter={80} align="center">
            <Grid.Col span={{ base: 12, md: 6 }} order={{ base: 2, md: 1 }}>
              <Box
                p="xl"
                bg="blue.0"
                style={{
                  borderRadius: "30% 70% 70% 30% / 30% 30% 70% 70%",
                  aspectRatio: "1/1",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                role="img"
                aria-label={t("storyPara3Box", "Swiss Quality")}
              >
                <Icons.IconCertificate
                  size={80}
                  stroke={1}
                  color="var(--mantine-color-blue-6)"
                  aria-hidden="true"
                />
                <Text fw={800} size="xl" mt="md" tt="uppercase" lts={1}>
                  {t("storyPara3Box", "Swiss Quality")}
                </Text>
              </Box>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }} order={{ base: 1, md: 2 }}>
              <Title order={2} mb="md" size="h1">
                {t("storyPara3Title", "Crafting Authenticity")}
              </Title>
              <Stack gap="md">
                <Text size="lg" lh={1.8} c="gray.8">
                  {t("storyPara3")}
                </Text>

                {/* Typography-Based Highlight */}
                <Text size="lg" lh={1.8} c="gray.8">
                  {t(
                    "storyPara4",
                    "Since then, we have been amazed by the versatility of our paneer. "
                  )}
                  <Text
                    component="span"
                    c="blue.7"
                    fw={700}
                    style={{
                      fontSize: rem(20),
                      fontStyle: "italic",
                      display: "inline-block",
                    }}
                  >
                    {t(
                      "highlightText",
                      "Whether on the grill, in salads or in classic Indian dishes, paneer is a culinary enrichment and at the same time a protein- and calcium-rich healthy alternative."
                    )}
                  </Text>
                </Text>
              </Stack>
            </Grid.Col>
          </Grid>
        </Stack>
      </Container>

      {/* 3. FINAL ALTERNATING SECTION */}
      <Container size="lg" mt={rem(120)} pb={rem(60)}>
        <Grid gutter={rem(80)} align="center">
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap="xl">
              <Box>
                <Title order={3} mb="xs" size={rem(32)} fw={800}>
                  {t("feedbackTitle", "We Love Your Feedback!")}
                </Title>
                <Text size="lg" c="gray.7" lh={1.6}>
                  {t(
                    "feedbackText",
                    "Share your experiences with us. We'd love to hear how you're enjoying our products."
                  )}
                </Text>
              </Box>

              <Button
                component="a"
                href="mailto:yogipaneer@gmail.com"
                variant="outline"
                size="xl"
                radius="xl"
                leftSection={<Icons.IconMail size={22} aria-hidden="true" />}
                style={{ alignSelf: "flex-start" }}
                aria-label={t("aboutContactAriaLabel", "Send email to yogipaneer at gmail.com")}
              >
                yogipaneer@gmail.com
              </Button>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Box
              p={rem(60)}
              bg="blue.0"
              style={{
                borderRadius: "70% 30% 30% 70% / 70% 70% 30% 30%",
                textAlign: "center",
                aspectRatio: "1/1",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Icons.IconQuote
                size={50}
                color="var(--mantine-color-blue-6)"
                opacity={0.3}
                style={{ marginBottom: rem(20) }}
                aria-hidden="true"
              />
              <Title order={2} size="2.2rem" mb="md" fw={900} lh={1.2}>
                {t("quoteText", "Great food needs great ingredients")}
              </Title>
              <Text size="lg" c="gray.8" lh={1.6} fw={500}>
                {t(
                  "quoteText1",
                  "Handcrafted Paneer, Ghee & Yogurt made fresh daily from premium Swiss milk."
                )}
              </Text>
            </Box>
          </Grid.Col>
        </Grid>
      </Container>

      {/* 4. FINAL SIGNATURE */}
      <Container size="sm" ta="center">
        <Stack gap={0}>
          <Text
            fw={900}
            size={rem(32)}
            variant="gradient"
            gradient={{ from: "blue", to: "cyan" }}
          >
            {t("signature", "Devesh & Vita")}
          </Text>
          <Text c="dimmed" tt="uppercase" lts={2} size="xs" fw={700}>
            {t("location", "Zug, Switzerland")}
          </Text>
        </Stack>
      </Container>
    </Box>
  );
}
